import { isAbdaMode, BLOCKED_WS } from '../../runtime/abdaRuntime';

type Listener = (ev: MessageEvent) => void;
type ReconnectListener = () => void;

type ConnState = {
  ws: WebSocket;
  url: string;
  listeners: Set<Listener>;
  reconnectListeners: Set<ReconnectListener>;
  reconnectAttempts: number;
  closedByClient: boolean;
  heartbeatTimer?: number;
  lastPong: number;
};

const conns = new Map<string, ConnState>();
let isTabVisible = true;

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    isTabVisible = document.visibilityState === "visible";
    if (isTabVisible) {
      conns.forEach((state, key) => {
        if (state.ws.readyState !== WebSocket.OPEN && !state.closedByClient) {
          connect(key, state.url);
        }
      });
    }
  });
}

function backendBase(): string {
  const raw =
    import.meta.env.VITE_WS_BASE ??
    import.meta.env.VITE_BACKEND_WS ??
    `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}`;
  return raw.endsWith("/") ? raw.slice(0, -1) : raw;
}

function buildUrl(path: string): string {
  if (!path.startsWith("/")) path = "/" + path;
  const base = backendBase();
  return `${base}${path}`;
}

function startHeartbeat(key: string, state: ConnState) {
  stopHeartbeat(state);
  state.heartbeatTimer = window.setInterval(() => {
    try {
      if (Date.now() - state.lastPong > 45000) {
        try { state.ws.close(4000, "heartbeat-timeout"); } catch {}
        return;
      }
      state.ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
    } catch {}
  }, 15000);
}

function stopHeartbeat(state: ConnState) {
  if (state.heartbeatTimer) {
    clearInterval(state.heartbeatTimer);
    state.heartbeatTimer = undefined;
  }
}

function scheduleReconnect(key: string, state: ConnState) {
  if (state.closedByClient) return;
  if (!isTabVisible) return;
  const delay = Math.min(16000, 500 * Math.pow(1.8, state.reconnectAttempts));
  state.reconnectAttempts++;
  window.setTimeout(() => connect(key, state.url), delay);
}

function connect(key: string, url: string) {
  if (isAbdaMode() && BLOCKED_WS.some(p => key.startsWith(p))) {
    if (import.meta.env.MODE !== 'production') {
      console.warn(`[ABDA] Blocked legacy WS connect: ${key}`);
    }
    const stubWs = {
      readyState: 3, // CLOSED
      send: () => {},
      close: () => {},
      addEventListener: () => {},
      removeEventListener: () => {},
    } as unknown as WebSocket;
    
    const stubState: ConnState = {
      ws: stubWs,
      url,
      listeners: new Set(),
      reconnectListeners: new Set(),
      reconnectAttempts: 0,
      closedByClient: true,
      lastPong: Date.now(),
    };
    conns.set(key, stubState);
    return stubState;
  }

  let state = conns.get(key);
  if (state && state.ws.readyState === WebSocket.OPEN) return state;

  const isReconnect = !!state;
  const ws = new WebSocket(url);
  if (!state) {
    state = {
      ws,
      url,
      listeners: new Set(),
      reconnectListeners: new Set(),
      reconnectAttempts: 0,
      closedByClient: false,
      lastPong: Date.now(),
    };
    conns.set(key, state);
  } else {
    state.ws = ws;
  }

  ws.addEventListener("open", () => {
    state!.reconnectAttempts = 0;
    state!.lastPong = Date.now();
    startHeartbeat(key, state!);
    
    if (isReconnect) {
      state!.reconnectListeners.forEach((fn) => fn());
    }
  });

  ws.addEventListener("message", (ev) => {
    try {
      const data = JSON.parse(ev.data);
      if (data?.type === "pong") {
        state!.lastPong = Date.now();
        return;
      }
    } catch {}
    state!.listeners.forEach((fn) => fn(ev));
  });

  ws.addEventListener("close", () => {
    stopHeartbeat(state!);
    if (!state!.closedByClient) scheduleReconnect(key, state!);
  });

  ws.addEventListener("error", () => {
    try { ws.close(); } catch {}
  });

  return state;
}

export function getSharedSocket(path: string) {
  const key = path.startsWith("/") ? path : `/${path}`;
  const url = buildUrl(key);
  const existing = conns.get(key);
  if (existing) return existing.ws;
  return connect(key, url)!.ws;
}

export function addMessageListener(path: string, listener: Listener) {
  const key = path.startsWith("/") ? path : `/${path}`;
  const state = conns.get(key) ?? connect(key, buildUrl(key))!;
  state.listeners.add(listener);
  return () => {
    state.listeners.delete(listener);
  };
}

export function addReconnectListener(path: string, listener: ReconnectListener) {
  const key = path.startsWith("/") ? path : `/${path}`;
  const state = conns.get(key) ?? connect(key, buildUrl(key))!;
  state.reconnectListeners.add(listener);
  return () => {
    state.reconnectListeners.delete(listener);
  };
}

export function send(path: string, payload: unknown) {
  const ws = getSharedSocket(path);
  if (ws.readyState === WebSocket.OPEN) {
    ws.send(typeof payload === "string" ? payload : JSON.stringify(payload));
  }
}

export function closeSharedSocket(path: string) {
  const key = path.startsWith("/") ? path : `/${path}`;
  const state = conns.get(key);
  if (!state) return;
  state.closedByClient = true;
  stopHeartbeat(state);
  try { state.ws.close(1000, "client-closed"); } catch {}
  conns.delete(key);
}
